/**
 * This device.
 *
 * Each till keeps its own id and its own receipt counter in local storage. The
 * counter is what makes offline receipt numbers safe: T1-000123 can only ever be
 * produced by till T1, so two tills selling with no internet cannot collide.
 *
 * The id and name belong to the browser. The receipt series and counter belong
 * to the SHOP: each shop numbers its own receipts from its own server record.
 */
import { newId, formatReceiptNo } from '@pos/shared';
import { keyIn, shopKey } from './shop.js';

const ID_KEY = 'pos.device.id';
const NAME_KEY = 'pos.device.name';
const PREFIX_KEY = 'pos.device.prefix';
const COUNTER_KEY = 'pos.device.counter';

export function deviceId(): string {
  let id = localStorage.getItem(ID_KEY);
  if (!id) {
    id = newId();
    localStorage.setItem(ID_KEY, id);
  }
  return id;
}

export function deviceName(): string {
  return localStorage.getItem(NAME_KEY) ?? '';
}
export function setDeviceName(name: string): void {
  localStorage.setItem(NAME_KEY, name);
}

/** `storageKey` names another shop; left out, the shop this page belongs to. */
const k = (key: string, storageKey?: string) => (storageKey === undefined ? shopKey(key) : keyIn(storageKey, key));

export function seriesPrefix(): string {
  return localStorage.getItem(k(PREFIX_KEY)) ?? 'T0';
}
export function setSeriesPrefix(p: string, storageKey?: string): void {
  localStorage.setItem(k(PREFIX_KEY, storageKey), p);
}

function counter(storageKey?: string): number {
  return Number(localStorage.getItem(k(COUNTER_KEY, storageKey)) ?? '0');
}

/** Keep the local counter at least as high as the server has seen. */
export function syncCounter(serverLast: number, storageKey?: string): void {
  if (serverLast > counter(storageKey)) localStorage.setItem(k(COUNTER_KEY, storageKey), String(serverLast));
}

/**
 * Take the next receipt number. Written to local storage BEFORE the sale is
 * saved, so a power cut cannot hand the same number out twice.
 */
export function takeReceiptNumber(): { receiptNo: string; counter: number } {
  const next = counter() + 1;
  localStorage.setItem(k(COUNTER_KEY), String(next));
  return { receiptNo: formatReceiptNo(seriesPrefix(), next), counter: next };
}

export function peekReceiptNumber(): string {
  return formatReceiptNo(seriesPrefix(), counter() + 1);
}
